import { createContext, useContext, useState } from 'react';
import { useFavoriteRewards } from 'hooks/useRewards/useFavoriteRewards';
import { RewardCard } from 'ui/types/RewardCard';
import { useRouter } from 'next/router';
interface RewardsContextType {
  isCashoutOpen: boolean;
  setIsCashoutOpen: (isOpen: boolean) => void;
  selectedReward: RewardCard | null;
  handleFavoriteClick: (reward: RewardCard) => Promise<void>;
  handleSearchSubmit: (searchQuery?: string) => void;
  handleRewardClick: (reward: RewardCard) => void;
  activeFilter: {
    label: string;
    value: string;
  } | null;
  handleFilterChange: (filter?: {
    label: string;
    value: string;
  }) => void;
  setSelectedReward: (reward: RewardCard | null) => void;
  filteredRewards: RewardCard[];
  handleShowMore: (rewards: RewardCard[]) => void;
}
export const RewardsContext = createContext<RewardsContextType | undefined>(undefined);
export const useRewards = (): RewardsContextType => {
  const context = useContext(RewardsContext);
  if (!context) {
    throw new Error('useRewards must be used within a RewardsProvider');
  }
  return context;
};
export const RewardsProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();

  // State
  const [isCashoutOpen, setIsCashoutOpen] = useState(false);
  const [filteredRewards, setFilteredRewards] = useState<RewardCard[]>([]);
  const [selectedReward, setSelectedReward] = useState<RewardCard | null>(null);
  const [activeFilter, setActiveFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const handleRewardClick = (reward: RewardCard) => {
    setSelectedReward(reward);
  };
  const handleShowMore = (rewards: RewardCard[]) => {
    setFilteredRewards(rewards);
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  };

  // Favorites
  const {
    addFavorite,
    removeFavorite
  } = useFavoriteRewards();
  const handleFavoriteClick = (reward: RewardCard): Promise<void> => reward.rewardFavouriteId ? removeFavorite(reward) : addFavorite(reward);

  // Search
  const handleSearchSubmit = (searchQuery?: string) => {
    let query = {
      ...router.query
    };
    if (!searchQuery) {
      delete query.search;
    } else {
      query.search = searchQuery;
    }
    router.push({
      pathname: router.pathname,
      query
    }, undefined, {
      shallow: true
    });
  };
  const handleFilterClear = () => {
    setActiveFilter(null);
    let query = {
      ...router.query
    };
    delete query.category;
    router.push({
      pathname: router.pathname,
      query
    }, undefined, {
      shallow: true
    });
  };
  const handleFilterChange = (filter: {
    label: string;
    value: string;
  }) => {
    if (!filter) {
      handleFilterClear();
      return;
    }
    setActiveFilter(filter);
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        category: filter.value
      }
    }, undefined, {
      shallow: true
    });
  };
  return <RewardsContext.Provider value={{
    activeFilter,
    isCashoutOpen,
    setIsCashoutOpen,
    handleFavoriteClick,
    handleSearchSubmit,
    handleRewardClick,
    handleFilterChange,
    selectedReward,
    setSelectedReward,
    filteredRewards,
    handleShowMore
  }} data-sentry-element="unknown" data-sentry-component="RewardsProvider" data-sentry-source-file="RewardsProvider.tsx">
      {children}
    </RewardsContext.Provider>;
};